import { useEffect, useRef } from "react";
import styled from "styled-components";
import { motion } from "motion/react";

import { ACCENT_COLOR } from "../theme";

export default function WizardToggle({ label, checked, onChange, description, icon, alt, last, compact, modeValue, onModeChange }) {
  const isRequire = modeValue === "require";

  return (
    <Row $alt={alt} $last={last} $compact={compact} onClick={() => onChange(!checked)}>
      {icon && (
        <IconCircle $compact={compact}>
          <i className={`fas ${icon}`} />
        </IconCircle>
      )}
      <TextWrap>
        <Label $compact={compact}>{label}</Label>
        {description && <Description $compact={compact}>{description}</Description>}
      </TextWrap>
      {checked && onModeChange && (
        <ModePill
          onClick={(e) => { e.stopPropagation(); onModeChange(isRequire ? "ask" : "require"); }}
          $require={isRequire}
        >
          <ModePillSlider mode={isRequire ? "require" : "ask"} />
        </ModePill>
      )}
      <Checkbox $on={checked} $compact={compact}>
        {checked && <i className="fas fa-check" />}
      </Checkbox>
    </Row>
  );
}

function ModePillSlider({ mode }) {
  const hasAnimated = useRef(false);
  const isRequire = mode === "require";
  const t = hasAnimated.current
    ? { duration: 0.2, ease: [0.4, 0, 0.2, 1] }
    : { duration: 0 };

  useEffect(() => { hasAnimated.current = true; }, []);

  return (
    <SliderWrap>
      <motion.span animate={{ y: isRequire ? -22 : 0 }} transition={t}>
        Optional
      </motion.span>
      <motion.span animate={{ y: isRequire ? 0 : 22 }} transition={t}>
        Required
      </motion.span>
    </SliderWrap>
  );
}

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: ${(p) => (p.$compact ? "8px" : "12px")};
  cursor: pointer;
  padding: ${(p) => (p.$compact ? "8px 16px" : "14px 24px")};
  background: ${(p) => (p.$alt ? "#f5f5f5" : "#fff")};
  border-bottom: ${(p) => (p.$last ? "none" : "1px solid rgba(0, 0, 0, 0.06)")};
  transition: background 0.15s;
`;

const Checkbox = styled.div`
  width: ${(p) => (p.$compact ? "20px" : "26px")};
  height: ${(p) => (p.$compact ? "20px" : "26px")};
  border-radius: ${(p) => (p.$compact ? "5px" : "6px")};
  border: 2px solid ${(p) => (p.$on ? ACCENT_COLOR : "#d1d5db")};
  background: ${(p) => (p.$on ? ACCENT_COLOR : "#fff")};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 0.15s, border-color 0.15s;
  color: #fff;
  font-size: ${(p) => (p.$compact ? "10px" : "14px")};
`;

const TextWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
`;

const Label = styled.div`
  font-size: ${(p) => (p.$compact ? "15px" : "18px")};
  color: #182941;
`;

const Description = styled.div`
  font-size: ${(p) => (p.$compact ? "12px" : "15px")};
  color: #6b7280;
`;

const ModePill = styled.button`
  appearance: none;
  border: none;
  border-radius: 100px;
  padding: 0;
  width: 62px;
  height: 22px;
  text-align: center;
  font-size: 11px;
  font-weight: 600;
  font-family: inherit;
  color: ${(p) => (p.$require ? ACCENT_COLOR : "#6b7280")};
  background: ${(p) => (p.$require ? "rgba(1, 96, 153, 0.12)" : "#e5e5e5")};
  cursor: pointer;
  flex-shrink: 0;
  overflow: hidden;
  transition: background 0.2s, color 0.15s;

  &:hover {
    background: ${(p) => (p.$require ? "rgba(1, 96, 153, 0.18)" : "#dedede")};
  }

  &:active ${() => SliderWrap} {
    transform: scale(0.9);
  }
`;

const SliderWrap = styled.div`
  position: relative;
  overflow: hidden;
  height: 22px;
  width: 100%;
  transition: transform 0.15s;

  span {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const IconCircle = styled.div`
  width: ${(p) => (p.$compact ? "30px" : "44px")};
  height: ${(p) => (p.$compact ? "30px" : "44px")};
  border-radius: 50%;
  background: ${ACCENT_COLOR};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #fff;
  font-size: ${(p) => (p.$compact ? "13px" : "20px")};
`;
